import React from 'react';
import { ScrollView, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';

import api from '@/utils/api';
import tw from '@/styles/tailwind';
import Text from '@/components/Text';

// ───────────────────────────────────────────────────────────────────────────
// Reporte "Inventario Vinte" (por comunidad, al cierre de un mes).
// Seguimiento de las viviendas que Vinte aún NO ha vendido pero están bajo
// resguardo de la AC y deben generar pago de mantenimiento.
//
// Cadena lógica (toda vivienda genera cuota; la paga el cliente si ya se
// entregó, o Vinte mientras sigue en inventario):
//   Inventario Vinte = Num viviendas − Entregadas
//   Ingresos:  Cliente = cuota × entregadas ; Vinte = cuota × inventario
//
// Fuente: GET /vinte/communities/{id}/reports/inventarios?periodo=YYYY-MM
//   • Privada            → Units agrupadas por street_name.
//   • numViviendas       → count de units de la privada.
//   • viviendasEntregadas→ units con vinte_params.fecha_entrega_cliente ≤ fin del mes.
//   • 1ª Entrega/AC/Acta → min de fecha_entrega_cliente / _ac / _admin (agregado por privada).
//   • administrador      → community.settings->vinte->privadas[street].administrador.
//   • cuota              → FeeType.amount de la vivienda.
// inventarioVinte / ingresos se derivan en el cliente.
// ───────────────────────────────────────────────────────────────────────────

type Privada = {
  nombre: string;
  numViviendas: number;
  administrador: string | null;
  cuota: number;
  fechaPrimerEntrega: string | null;
  fechaEntregaAC: string | null;
  fechaFirmaActa: string | null;
  viviendasEntregadas: number;
};

type Row = Privada & {
  id: number;
  inventarioVinte: number;
  ingresoCliente: number;
  ingresoVinte: number;
};

function buildRow(p: Privada, i: number): Row {
  const inventarioVinte = p.numViviendas - p.viviendasEntregadas;
  return {
    ...p,
    id: i + 1,
    inventarioVinte,
    ingresoCliente: p.cuota * p.viviendasEntregadas,
    ingresoVinte: p.cuota * inventarioVinte,
  };
}

const money = (n: number) => n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });

const MESES_ABBR = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const fmtDate = (iso?: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}-${MESES_ABBR[+m - 1]}-${y.slice(2)}`;
};

// ─── Definición de columnas ──────────────────────────────────────────────────
type Align = 'left' | 'right' | 'center';
type Col = {
  key: keyof Row;
  label: string;
  w: number;
  align?: Align;
  money?: boolean;
  date?: boolean;
  total?: boolean;
  blue?: boolean;
  group?: 'Ingresos por';
};

const COLS: Col[] = [
  { key: 'id', label: 'No.', w: 44, align: 'center' },
  { key: 'nombre', label: 'Nombre de Privada', w: 190, align: 'left' },
  { key: 'numViviendas', label: 'No. Viv.', w: 64, align: 'right', total: true },
  { key: 'administrador', label: 'Administrador', w: 130, align: 'left' },
  { key: 'cuota', label: 'Cuota', w: 80, align: 'right', money: true, blue: true },
  { key: 'fechaPrimerEntrega', label: '1ª Entrega', w: 96, date: true },
  { key: 'fechaEntregaAC', label: 'Entrega AC', w: 96, date: true },
  { key: 'fechaFirmaActa', label: 'Firma Acta', w: 96, date: true },
  { key: 'viviendasEntregadas', label: 'Entregadas', w: 84, align: 'right', total: true, blue: true },
  { key: 'inventarioVinte', label: 'Inventario Vinte', w: 96, align: 'right', total: true },
  { key: 'ingresoCliente', label: 'Cliente', w: 116, align: 'right', money: true, total: true, group: 'Ingresos por' },
  { key: 'ingresoVinte', label: 'Vinte', w: 104, align: 'right', money: true, total: true, group: 'Ingresos por' },
];

// Segmentos de la cabecera superior (agrupa columnas con el mismo `group`).
function topSegments() {
  const segs: { label: string; w: number; grouped: boolean }[] = [];
  let i = 0;
  while (i < COLS.length) {
    const g = COLS[i].group;
    if (!g) {
      segs.push({ label: '', w: COLS[i].w, grouped: false });
      i++;
    } else {
      let w = 0;
      while (i < COLS.length && COLS[i].group === g) { w += COLS[i].w; i++; }
      segs.push({ label: g, w, grouped: true });
    }
  }
  return segs;
}

const display = (col: Col, v: any) => {
  if (col.date) return fmtDate(v);
  if (col.money) return money((v as number) ?? 0);
  if (typeof v === 'number') return v.toLocaleString('es-MX');
  return v ?? '—';
};

function HCell({ col }: { col: Col }) {
  return (
    <View
      style={tw.style(
        `px-2 py-2 justify-end border-r border-gray-200`,
        { width: col.w },
        col.blue ? `bg-blue-50` : `bg-secondary/10`,
        col.align === 'right' ? `items-end` : col.align === 'center' ? `items-center` : `items-start`,
      )}
    >
      <Text style={tw`text-secondary font-600 text-xs text-center`} numberOfLines={2}>{col.label}</Text>
    </View>
  );
}

function BCell({ col, value, total }: { col: Col; value: any; total?: boolean }) {
  return (
    <View
      style={tw.style(
        `px-2 py-2 justify-center border-r border-b border-gray-100`,
        { width: col.w },
        col.blue && `bg-blue-50/50`,
        total && `bg-gray-50`,
        col.align === 'right' ? `items-end` : col.align === 'center' ? `items-center` : `items-start`,
      )}
    >
      <Text style={tw.style(`text-xs`, total ? `text-gray-900 font-700` : `text-gray-700`)} numberOfLines={1}>
        {value}
      </Text>
    </View>
  );
}

export default function Inventarios({ communityId, date }: { communityId?: string; date?: Date }) {
  const periodo = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}` : undefined;
  const periodoLabel = date ? date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }) : 'Actual';

  const { isLoading, error, data } = useQuery({
    queryKey: ['vinte.reports.inventarios', communityId, periodo],
    queryFn: () =>
      api.get(`/vinte/communities/${communityId}/reports/inventarios`, { params: { periodo } }).then(r => r.data),
    enabled: !!communityId,
  });

  const rows: Row[] = (data?.privadas ?? []).map(buildRow);

  // Totales por columna (solo las marcadas con total).
  const totals = {} as Record<string, number>;
  for (const col of COLS) {
    if (col.total) totals[col.key] = rows.reduce((acc, r) => acc + ((r[col.key] as number) ?? 0), 0);
  }

  return (
    <View style={tw`px-4 pb-10`}>
      <View style={tw`row justify-between items-center mb-4`}>
        <View>
          <Text style={tw`text-lg font-700 text-secondary`}>Inventario Vinte</Text>
          <Text style={tw`text-gray-400 text-xs`}>{data?.community ?? '—'}</Text>
        </View>
        <Text style={tw`text-gray-400`}>Al cierre de <Text style={tw`text-gray-700 font-600`}>{periodoLabel}</Text></Text>
      </View>

      {isLoading && <Text style={tw`my-8 text-center text-gray-400`}>Cargando...</Text>}
      {!!error && <Text style={tw`my-8 text-center text-red-400`}>No se pudo cargar el reporte.</Text>}
      {!isLoading && !error && rows.length === 0 && (
        <Text style={tw`my-8 text-center text-gray-400`}>Sin viviendas Vinte registradas en esta comunidad.</Text>
      )}

      {rows.length > 0 && (
        <ScrollView horizontal showsHorizontalScrollIndicator style={tw`bg-white rounded-xl border border-gray-100`}>
          <View>
            {/* Cabecera superior (grupos) */}
            <View style={tw`row`}>
              {topSegments().map((seg, i) => (
                <View
                  key={i}
                  style={tw.style(
                    `px-2 py-1.5 justify-center items-center border-r border-gray-200`,
                    { width: seg.w },
                    seg.grouped && `bg-amber-100`,
                  )}
                >
                  {seg.grouped && <Text style={tw`text-gray-700 font-700 text-xs`}>{seg.label}</Text>}
                </View>
              ))}
            </View>

            {/* Cabecera de columnas */}
            <View style={tw`row border-b border-gray-300`}>
              {COLS.map(col => <HCell key={col.key} col={col} />)}
            </View>

            {/* Filas */}
            {rows.map((r, idx) => (
              <View key={r.id} style={tw.style(`row`, idx % 2 === 1 && `bg-gray-50/40`)}>
                {COLS.map(col => <BCell key={col.key} col={col} value={display(col, r[col.key])} />)}
              </View>
            ))}

            {/* Totales */}
            <View style={tw`row border-t-2 border-secondary/40`}>
              {COLS.map((col, i) => {
                if (i === 0) {
                  return (
                    <View key={col.key} style={tw.style(`px-2 py-2 justify-center bg-gray-50`, { width: COLS[0].w + COLS[1].w })}>
                      <Text style={tw`text-secondary font-700 text-xs`}>TOTALES</Text>
                    </View>
                  );
                }
                if (i === 1) return null; // ya cubierto por el span de arriba
                return (
                  <BCell key={col.key} col={col} total value={col.total ? display(col, totals[col.key]) : ''} />
                );
              })}
            </View>
          </View>
        </ScrollView>
      )}
    </View>
  );
}
